import { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { formatPriceCents } from "@/components/services/service-meta";
import { agruparMateriais, rotuloDoMaterial } from "@/lib/agrupar-materiais";

const PADRAO = "__padrao__";

type Linha = {
  materialLineId: string;
  rotulo: string;
  unidade: string | null;
  precoCents: number | null;
  quantidade: string;
  fator: string;
};

const paraNumero = (v: string) => Number((v || "0").replace(",", "."));

/**
 * Quanto a máquina gasta de consumível, por material.
 *
 * O seletor de cima escolhe de que material se está falando. "Padrão" é a linha de escape: vale
 * pra qualquer material que não tenha gasto próprio — e o específico SUBSTITUI o padrão, não soma,
 * senão detalhar o cadastro sairia mais caro que não detalhar.
 */
export function EquipamentoGastoTab({
  equipmentId,
  equipmentName,
  materialOptionIds,
}: {
  equipmentId: string;
  equipmentName: string;
  materialOptionIds: string[];
}) {
  const utils = trpc.useUtils();
  const [materialOptionId, setMaterialOptionId] = useState<string | null>(null);
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [sujo, setSujo] = useState(false);

  const listaQuery = trpc.equipamento.listar.useQuery();
  const ofertasQuery = trpc.prosthesis.catalog.materialOptions.useQuery();
  const materiaisQuery = trpc.prosthesis.catalog.myMaterialLines.useQuery();

  const equipamento = (listaQuery.data ?? []).find((e: any) => e.id === equipmentId);
  const doEquipamento = (ofertasQuery.data ?? []).filter((o: any) => materialOptionIds.includes(o.id));

  const materiais = useMemo(
    () => (materiaisQuery.data ?? []).filter((m: any) => m.active !== false),
    [materiaisQuery.data],
  );
  const grupos = useMemo(() => agruparMateriais(materiais as any[]), [materiais]);

  // Carrega o gasto do material escolhido. `sujo` protege o que foi digitado de um refetch.
  useEffect(() => {
    if (!equipamento || sujo) return;
    const doMaterial = (equipamento.gastos ?? []).filter(
      (g: any) => (g.materialOptionId ?? null) === materialOptionId,
    );
    setLinhas(
      doMaterial.map((g: any) => ({
        materialLineId: g.materialLine.id,
        rotulo: g.materialLine.name,
        unidade: g.unit ?? null,
        precoCents: g.materialLine.purchasePriceCents,
        quantidade: String(g.quantityNet).replace(".", ","),
        fator: String(g.correctionFactor).replace(".", ","),
      })),
    );
  }, [equipamento, materialOptionId, sujo]);

  const salvar = trpc.equipamento.salvarGasto.useMutation({
    onSuccess: () => {
      toast.success("Gasto salvo.");
      setSujo(false);
      utils.equipamento.listar.invalidate();
      // O custo da ficha muda junto — é dele que sai o número na tela do serviço.
      utils.fichaTecnica.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao salvar o gasto."),
  });

  function adicionar(id: string) {
    if (!id) return;
    const m = (materiais as any[]).find((x) => x.id === id);
    if (!m || linhas.some((l) => l.materialLineId === id)) return;
    setSujo(true);
    setLinhas((v) => [
      ...v,
      {
        materialLineId: m.id,
        rotulo: rotuloDoMaterial(m),
        unidade: m.unit ?? null,
        precoCents: m.purchasePriceCents ?? null,
        quantidade: "1",
        fator: m.defaultCorrectionFactor != null ? String(m.defaultCorrectionFactor).replace(".", ",") : "1",
      },
    ]);
  }

  const total = linhas.reduce((soma, l) => {
    const bruta = paraNumero(l.quantidade) * (paraNumero(l.fator) || 1);
    return soma + (l.precoCents != null ? bruta * l.precoCents : 0);
  }, 0);

  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-zinc-50 px-3 py-2 dark:bg-muted/40">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
          Gasto de
        </p>
        <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">{equipmentName}</p>
      </div>

      <div>
        <p className="mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground">Material</p>
        <select
          value={materialOptionId ?? PADRAO}
          onChange={(e) => {
            if (sujo && !window.confirm("Você tem alterações não salvas neste gasto. Descartar?")) return;
            setSujo(false);
            setMaterialOptionId(e.target.value === PADRAO ? null : e.target.value);
          }}
          className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm dark:border-border dark:bg-card dark:text-foreground"
        >
          <option value={PADRAO}>Padrão (qualquer material)</option>
          {doEquipamento.map((o: any) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
        <p className="mt-1 text-[11px] leading-snug text-zinc-500 dark:text-muted-foreground">
          O gasto específico de um material substitui o padrão — não soma. Fresar dissilicato pode
          comer 0,3 broca onde o padrão é 0,1.
        </p>
      </div>

      <table className="w-full table-fixed text-xs">
        <thead className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 dark:border-border dark:text-muted-foreground">
          <tr>
            <th className="pb-1.5 text-left font-semibold">Insumo</th>
            <th className="w-[4.5rem] pb-1.5 text-right font-semibold">Qtd.</th>
            <th className="w-14 pb-1.5 text-right font-semibold">Fator</th>
            <th className="w-16 pb-1.5 text-right font-semibold">Bruta</th>
            <th className="w-20 pb-1.5 text-right font-semibold">Custo</th>
            <th className="w-7" />
          </tr>
        </thead>
        <tbody>
          {linhas.map((l, i) => {
            const bruta = paraNumero(l.quantidade) * (paraNumero(l.fator) || 1);
            return (
              <tr key={l.materialLineId} className="border-b border-zinc-100 dark:border-border/50">
                <td className="truncate py-1.5 pr-2 text-zinc-800 dark:text-foreground/90" title={l.rotulo}>
                  {l.rotulo}
                  {l.precoCents == null && (
                    <span className="ml-1 text-[10px] text-amber-600 dark:text-amber-400">sem preço</span>
                  )}
                </td>
                {(["quantidade", "fator"] as const).map((campo) => (
                  <td key={campo} className="py-1.5 pl-1 text-right">
                    <input
                      value={l[campo]}
                      inputMode="decimal"
                      aria-label={`${campo === "quantidade" ? "Quantidade" : "Fator"} de ${l.rotulo}`}
                      onChange={(e) => {
                        setSujo(true);
                        setLinhas((v) => v.map((x, j) => (j === i ? { ...x, [campo]: e.target.value } : x)));
                      }}
                      className="h-7 w-full rounded border border-zinc-200 bg-white px-1.5 text-right text-xs tabular-nums dark:border-border dark:bg-card dark:text-foreground"
                    />
                  </td>
                ))}
                <td className="py-1.5 pl-1 text-right tabular-nums text-zinc-500 dark:text-muted-foreground">
                  {bruta.toLocaleString("pt-BR", { maximumFractionDigits: 4 })}
                  {l.unidade && <span className="ml-0.5 text-[10px]">{l.unidade}</span>}
                </td>
                <td className="py-1.5 pl-1 text-right tabular-nums text-zinc-800 dark:text-foreground/90">
                  {l.precoCents != null ? formatPriceCents(Math.round(bruta * l.precoCents)) : "—"}
                </td>
                <td className="py-1.5 text-right">
                  <button
                    type="button"
                    aria-label={`Remover ${l.rotulo}`}
                    onClick={() => {
                      setSujo(true);
                      setLinhas((v) => v.filter((_, j) => j !== i));
                    }}
                    className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <select
        value=""
        onChange={(e) => adicionar(e.target.value)}
        className="h-9 w-full rounded-md border border-dashed border-zinc-300 bg-white px-3 text-sm text-zinc-600 dark:border-border dark:bg-card dark:text-muted-foreground"
      >
        <option value="">+ Adicionar insumo</option>
        {grupos.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.itens.map((m: any) => (
              <option key={m.id} value={m.id}>
                {rotuloDoMaterial(m)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      <div className="flex items-center justify-between gap-2 border-t border-zinc-100 pt-3 dark:border-border">
        <p className="text-xs text-zinc-600 dark:text-muted-foreground">
          Custo por trabalho:{" "}
          <span className="font-semibold tabular-nums text-zinc-900 dark:text-foreground">
            {formatPriceCents(Math.round(total))}
          </span>
        </p>
        <div className="flex items-center gap-2">
          {sujo && <span className="text-[11px] text-amber-600 dark:text-amber-400">Alterações não salvas</span>}
          <button
            type="button"
            disabled={salvar.isPending || !sujo}
            onClick={() =>
              salvar.mutate({
                equipmentId,
                materialOptionId,
                lines: linhas.map((l) => ({
                  materialLineId: l.materialLineId,
                  quantityNet: l.quantidade,
                  correctionFactor: l.fator || "1",
                })),
              })
            }
            className={cn(
              "inline-flex h-8 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-xs font-semibold text-white",
              "disabled:opacity-50",
            )}
          >
            {salvar.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Salvar gasto
          </button>
        </div>
      </div>
    </div>
  );
}
